import Button from "components/Button";
import InputField from "components/InputField";
import StepIndicator from "components/StepIndicator";
import React, { ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router-dom";
import { MAX_WIDTH_MOBILE } from "utils/constant";
import { validateBookingNo, validatePhoneNumber } from "utils/validator";
import styles from "./Form.module.scss";

interface FormProps {
  loading: boolean;
  createBookingUrl: string;
  setIsFormValuesDirty: (isDirty: boolean) => void;
  formValues: {
    countryCode: string;
    mobileNo: string;
    bookingNo: string;
  };
  onChangeFormValues: (values: {
    countryCode: string;
    mobileNo: string;
    bookingNo: string;
  }) => void;
  submit: (otpSid?: string, onError?: () => void) => void;
}

const Form: React.FC<FormProps> = ({
  loading,
  createBookingUrl,
  setIsFormValuesDirty,
  formValues,
  onChangeFormValues,
  submit,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ maxWidth: MAX_WIDTH_MOBILE });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [errors, setErrors] = useState<{
    countryCode?: string;
    mobileNo?: string;
    bookingNo?: string;
  }>({
    countryCode: undefined,
    mobileNo: undefined,
    bookingNo: undefined,
  });

  const isAllRequiredFilled =
    !!formValues.countryCode && !!formValues.mobileNo && !!formValues.bookingNo;

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIsFormValuesDirty(true);
    const { name, value } = e.target;

    onChangeFormValues({
      ...formValues,
      [name]: value,
    });

    if (submitted) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const validateAllFields = () => {
    const errors = {
      mobileNo: validatePhoneNumber(
        formValues.countryCode,
        formValues.mobileNo
      ),
      bookingNo: validateBookingNo(formValues.bookingNo),
    };
    setErrors(errors);

    if (Object.values(errors).some((error) => error !== undefined)) {
      const firstErrorName =
        Object.entries(errors).find(
          ([key, value]) => value !== undefined
        )?.[0] || null;
      const firstErrorElement = firstErrorName
        ? document.getElementsByName(firstErrorName)[0]
        : null;
      if (firstErrorElement) {
        setTimeout(() => {
          firstErrorElement.focus({ preventScroll: true });
          window.scrollTo({
            top:
              firstErrorElement.getBoundingClientRect().top +
              window.scrollY -
              46,
            behavior: "smooth",
          });
        }, 0);
      }
    }
    return !Object.values(errors).some((error) => error !== undefined);
  };

  return (
    <div
      className={`${styles.container} ${isMobile ? styles.mobile : ""} ${
        submitting ? styles.submitting : ""
      }`}
    >
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <StepIndicator value={1} total={3} />
          <span className={styles.title}>{t("editOrCancelAppointment")}</span>
        </div>
        <Button
          type="secondary"
          onClick={() => navigate(createBookingUrl)}
          className={styles.button}
        >
          {t("createAppointment")}
        </Button>
      </div>
      <div className={styles.content}>
        {loading ? (
          <Skeleton width="100%" height={281} borderRadius={16} />
        ) : (
          <>
            <div className={styles.block}>
              <div className={styles.title}>{t("fillInAppointmentInfo")}</div>
              <div className={styles.detail}>
                <div className={styles.rows}>
                  <div className={styles.row}>
                    <InputField
                      type="tel"
                      name="mobileNo"
                      value={formValues.mobileNo}
                      error={errors.mobileNo}
                      onChange={handleChange}
                      title={t("mobileNumber")}
                      placeholder={`${t("mobileNumber")} (852)`}
                      reminders={[
                        `${t("verifyNumber")} (${t(
                          "onlyHongKongPhoneNumbersAreSupported"
                        )})`,
                      ]}
                      dropdownOptions={[
                        { name: "+852", value: "852" },
                        // { name: '+853', value: '853' },
                        // { name: '+86', value: '86' },
                      ]}
                      dropdownValue={
                        [
                          { name: "+852", value: "852" },
                          // { name: '+853', value: '853' },
                          // { name: '+86', value: '86' },
                        ].find((opt) => opt.value === formValues.countryCode)
                          ?.value
                      }
                      onChangeDropdownValue={(value) =>
                        onChangeFormValues({
                          ...formValues,
                          countryCode: value,
                        })
                      }
                      dropdownReadOnly
                    />
                  </div>
                </div>

                <div className={styles.rows}>
                  <div className={styles.row}>
                    <InputField
                      name="bookingNo"
                      value={formValues.bookingNo}
                      error={errors.bookingNo}
                      onChange={handleChange}
                      title={t("bookingNumber")}
                      placeholder="e.g., RA1234567"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.buttons}>
              <Button
                type="primary"
                disabled={!isAllRequiredFilled}
                loading={submitting}
                onClick={() => {
                  setSubmitted(true);
                  if (validateAllFields()) {
                    setSubmitting(true);
                    submit(undefined, () => {
                      setSubmitting(false);
                      setSubmitted(false);
                    });
                  }
                }}
              >
                <span>{t("confirm")}</span>
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Form;
